/**
 * API client for the Breakfast AI Twin Marketplace.
 * Mirrors the existing fetch+credentials pattern used across the app.
 */

import { refusalMessage } from '../../lib/chatRefusal';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface SubCategory {
  slug: string;
  name: string;
  emoji: string;
  archetype_count: number;
  twin_count: number;
}

export interface Archetype {
  archetype_id: string;
  name: string;
  sub_category: string;
  sub_category_name: string;
  emoji: string;
  twin_count: number;
}

export interface ArchetypeProfile extends Archetype {
  sample_profile: Record<string, string>;
}

export interface UseCase {
  key: 'survey' | 'concept' | 'packaging' | 'chat';
  name: string;
  blurb: string;
}

export interface MarketplaceState {
  free_study_used: boolean;
  twin_unit_price_cents: number;
}

export interface StudySelection {
  sub_category: string;
  archetype_id?: string;
  twin_count?: number;
  /** Friendly archetype name, captured at selection time so the review screen
   *  can show it without re-fetching the archetype list. */
  name?: string;
}

export interface Study {
  id: number;
  use_case: string;
  selections: StudySelection[];
  twin_count: number;
  is_free: boolean;
  amount_cents: number;
  status: string;
  payment_status: string;
  report_status: string;
  inputs?: Record<string, unknown>;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json();
}

async function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    // A 403 is a refusal carrying a reason — the plan's chat cap names it in a dict. The generic
    // branch below JSON.stringify()s that dict, so the user was shown raw JSON instead of the
    // sentence. refusalMessage returns the sentence, and never an empty string.
    if (res.status === 403) throw new Error(await refusalMessage(res));
    let detail = `${method} ${path} failed: ${res.status}`;
    try {
      const data = await res.json();
      if (data?.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}

// --- Marketplace auth (separate from the main platform AuthContext) ---
export interface MktUser {
  id: number;
  email: string;
  name?: string;
  is_admin?: boolean;
  account_type?: string;
  subscription_plan?: string;
}

// Raw POST that never throws — auth flows need the status/body (401/403/requires-verification).
async function authPost(path: string, body: unknown): Promise<{ ok: boolean; status: number; data: any }> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data: any = {};
  try { data = await res.json(); } catch { /* empty body */ }
  return { ok: res.ok, status: res.status, data };
}

export const mktSignup = (body: { name: string; email: string; password: string; company?: string; policies_accepted?: boolean }) =>
  authPost('/api/marketplace/auth/signup', body);
export const mktLogin = (email: string, password: string) =>
  authPost('/api/marketplace/auth/login', { email, password });
export const mktVerifyOtp = (email: string, otp_code: string, policies_accepted = true) =>
  authPost('/api/marketplace/auth/verify-otp', { email, otp_code, policies_accepted });
export const mktResendOtp = (email: string) =>
  authPost('/api/marketplace/auth/resend-otp', { email });
export const mktForgotPassword = (email: string) =>
  authPost('/api/marketplace/auth/forgot-password', { email });
export const mktResetPassword = (email: string, otp_code: string, new_password: string) =>
  authPost('/api/marketplace/auth/reset-password', { email, otp_code, new_password });
export const mktLogout = () => authPost('/api/marketplace/auth/logout', {});
export const mktMe = async (): Promise<MktUser | null> => {
  const res = await fetch(`${API_URL}/api/marketplace/auth/me`, { credentials: 'include' });
  if (!res.ok) return null;
  return res.json();
};

// --- Public browse ---
export const getUseCases = () => get<UseCase[]>('/api/marketplace/use-cases');
export const getSubCategories = () => get<SubCategory[]>('/api/marketplace/sub-categories');
export const getArchetypes = (slug: string) =>
  get<Archetype[]>(`/api/marketplace/sub-categories/${slug}/archetypes`);
export const getArchetypeProfile = (archetypeId: string) =>
  get<ArchetypeProfile>(`/api/marketplace/archetypes/${encodeURIComponent(archetypeId)}`);

// --- Marketplace population (single source of truth for participant counts) ---
// The true DISTINCT population across ALL categories, counted once server-side.
// A persona can belong to multiple breakfast categories, so summing per-category
// twin_counts double-counts; every user-facing headline number must come from here.
export interface PopulationResponse {
  total_twins?: number;
  population?: number;
  twin_count?: number;
}
export const getPopulation = async (): Promise<number> => {
  const data = await get<PopulationResponse>('/api/marketplace/population');
  return data.total_twins ?? data.population ?? data.twin_count ?? 0;
};

// --- Distinct twin count for a live selection ---
// A single persona can belong to MULTIPLE breakfast categories at once (e.g. the
// same person is a yogurt "Protein Optimizers" AND an oats "Protein Optimizers").
// Summing per-archetype bucket sizes therefore DOUBLE-COUNTS shared people and,
// because cost is billed per distinct profile, OVER-CHARGES. This endpoint hands
// the current selection to the server, which resolves + de-dupes the personas and
// returns the DISTINCT count (plus a per-sub-category breakdown). Always prefer
// this over any client-side reduce(sum) of twin_count.
export interface TwinCountSelection {
  sub_category: string;
  archetype_id?: string;
}
export interface TwinCountResult {
  /** Distinct personas across the whole selection (de-duped server-side). */
  twin_count: number;
  /** Distinct personas within each selected sub-category, keyed by slug. */
  by_sub_category?: Record<string, number>;
}
export const getSelectionTwinCount = (selections: TwinCountSelection[]) =>
  send<TwinCountResult>('/api/marketplace/twin-count', 'POST', { selections });

// --- Account state ---
export const getMarketplaceState = () => get<MarketplaceState>('/api/marketplace/me/state');

// --- Study lifecycle (gated) ---
export interface CreateStudyResult {
  study_id: number;
  is_free: boolean;
  twin_count: number;
  amount_cents: number;
  status: string;
  /** Echoed back only when the named study was actually retired (owned by the caller
   *  and still pre-payment); null when it was ineligible or not supplied. */
  superseded_study_id?: number | null;
}
/** `supersededStudyId` names a pre-payment study this one replaces — the user resumed an
 *  unpaid study and then changed the use case or audience, so its row cannot be reused.
 *  The server retires it and expires its Stripe checkout session. */
export const createStudy = (
  useCase: string, selections: StudySelection[], supersededStudyId?: number,
) =>
  send<CreateStudyResult>('/api/marketplace/studies', 'POST', {
    use_case: useCase,
    selections,
    ...(supersededStudyId ? { superseded_study_id: supersededStudyId } : {}),
  });

export const saveStudyInputs = (studyId: number, inputs: Record<string, unknown>) =>
  send<{ message: string }>(`/api/marketplace/studies/${studyId}`, 'PATCH', { inputs });

export const getStudy = (studyId: number) =>
  get<Study>(`/api/marketplace/studies/${studyId}`);

export interface SubmitResult {
  study_id: number;
  is_free: boolean;
  status: string;
  checkout_url?: string | null;
}
export const submitStudy = (studyId: number) =>
  send<SubmitResult>(`/api/marketplace/studies/${studyId}/submit`, 'POST');

export interface StudyResults {
  study_id: number;
  use_case: string;
  status: string;
  report_status?: string;
  twin_count?: number;
  simulation_id?: number | null;
  ready: boolean;
  results: unknown;
}
export const getStudyResults = (studyId: number) =>
  get<StudyResults>(`/api/marketplace/studies/${studyId}/results`);

// Dynamic study ETA (#39) — estimate persists across reload (computed from the
// server-side run start), so `remaining_seconds` is the true time left.
export interface StudyEta {
  study_id: number;
  ready: boolean;
  estimated_total_seconds: number;
  elapsed_seconds: number;
  remaining_seconds: number;
  soft_landing_seconds: number;
}
export const getStudyEta = (studyId: number) =>
  get<StudyEta>(`/api/marketplace/studies/${studyId}/eta`);

// Server-authoritative budget for chat-with-simulation on the results view (#33).
// The cap is enforced server-side; this lets the FE seed its counter so it stays
// correct across reloads / new tabs instead of resetting to zero.
export interface SimChatContext {
  queries_used: number;
  queries_allowed: number;
  /** A query being answered right now: real, but not yet committed. */
  queries_in_flight: number;
  queries_remaining: number;
}
export const getSimChatContext = (studyId: number) =>
  get<SimChatContext>(`/api/marketplace/studies/${studyId}/sim-chat-context`);

// Optimistically reflect one accepted sim-chat query in the quota object so the
// counter and cap advance immediately in the same render (the true figures are
// reseeded from the server on reload). Updates every derived field together so
// the object keeps its invariant (queries_remaining = allowed - used - in_flight)
// rather than leaving siblings stale. Returns `prev` untouched when the server
// context hasn't resolved yet (null) — in that window the caller advances the
// counter from the optimistic message instead. Shaped as a functional state
// updater so it can be passed straight to setState.
export const applyOptimisticSimQuery = (prev: SimChatContext | null): SimChatContext | null => {
  if (!prev) return prev;
  return {
    ...prev,
    queries_used: prev.queries_used + 1,
    queries_remaining: Math.max(0, prev.queries_remaining - 1),
  };
};

export interface StudyHistoryItem {
  id: number;
  use_case: string;
  /** Which audience this study ran against, e.g. ['Cereal: Protein Purists'] (#35). */
  audience_labels?: string[];
  status: string;
  report_status?: string;
  name?: string;
  twin_count?: number;
  amount_cents?: number;
  is_free?: boolean;
  created_at?: string;
  queries_used?: number;
  queries_allowed?: number;
  /** A query being answered right now: real, but not yet committed. */
  queries_in_flight?: number;
  queries_remaining?: number;
}
export const listStudies = () => get<StudyHistoryItem[]>('/api/marketplace/studies');

// --- Agentic chat (reuses the platform's /healthcare-chat-v2 backend) ---
export interface ChatContext {
  study_id: number;
  segment_id: number | null;
  session_id: number | null;
  queries_used: number;
  queries_allowed: number;
  /** A query being answered right now: real, but not yet committed. */
  queries_in_flight: number;
  queries_remaining: number;
}
export const getChatContext = (studyId: number) =>
  get<ChatContext>(`/api/marketplace/studies/${studyId}/chat-context`);

// Server-authoritative: resolves the study's selected profiles into an
// in-memory persona audience, creates the hc-v2 session bound to it, and
// returns the session id + query budget. Safe to call repeatedly (idempotent).
export interface ChatStart {
  session_id: number;
  twin_count?: number;
  cohort_labels?: string[];
  queries_used: number;
  queries_allowed: number;
  /** A query being answered right now: real, but not yet committed. */
  queries_in_flight: number;
  queries_remaining: number;
}
export const startChat = (studyId: number) =>
  send<ChatStart>(`/api/marketplace/studies/${studyId}/chat/start`, 'POST', {});

export const consumeChatQuery = (studyId: number, query: string, sessionId?: number | null) =>
  send<{ queries_used: number; queries_allowed: number; queries_remaining: number; queries_in_flight?: number }>(
    `/api/marketplace/studies/${studyId}/chat/consume`, 'POST', { query, session_id: sessionId });

export const createChatSession = (segmentId: number) =>
  send<{ id: number }>('/healthcare-chat-v2/sessions', 'POST', { segment_id: segmentId });

export const sendChatMessage = (sessionId: number, query: string) =>
  send<{ task_id: string }>(`/healthcare-chat-v2/sessions/${sessionId}/chat`, 'POST',
    { query, context_filtering_enabled: true });

export interface ChatTaskStatus {
  state: string; progress?: number; message?: string;
  result?: { response: string; format?: string; personas_cited?: string[] };
  error?: string;
}
export const getChatTaskStatus = (taskId: string) =>
  get<ChatTaskStatus>(`/healthcare-chat-v2/status/${taskId}`);

export const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
